import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Send, Sparkles, Menu, ChevronDown, Copy, User, Square, Bot, Paperclip, X, FileText, Image as ImageIcon, Loader2, Crown, RefreshCw, Share2, Download, Printer, Globe, Headphones } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import AppSidebar from '../components/AppSidebar';
import { Markdown } from '../components/Markdown';
import { SpeakButton, stopSpeaking } from '../components/SpeakButton';
import { MicButton } from '../components/MicButton';
import { HandsFreeBar } from '../components/HandsFreeBar';
import { useHandsFree } from '../hooks/useHandsFree';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MODELS } from '../mock/mock';
import { api, streamChat, fileUrl } from '../lib/api';

const AttachmentChip = ({ a, onRemove, light }) => {
  const isImg = a.content_type?.startsWith('image/');
  return (
    <span data-testid="attachment-chip" className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs max-w-[220px] ${light ? 'bg-white/15 text-primary-foreground' : 'bg-secondary text-foreground'}`}>
      {a.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : isImg ? <ImageIcon className="h-3.5 w-3.5 shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{a.filename}</span>
      {onRemove && <button onClick={onRemove} className="hover:text-destructive"><X className="h-3 w-3" /></button>}
    </span>
  );
};

const ChatApp = () => {
  const { t, lang } = useApp();
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [model, setModel] = useState(MODELS[0]);
  const [modelOpen, setModelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agent, setAgent] = useState(location.state?.agent || null);
  const [pending, setPending] = useState([]);
  const [shared, setShared] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [imageMode, setImageMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [autoSpeakId, setAutoSpeakId] = useState(null);
  const voiceRef = useRef(false);
  const handleSendRef = useRef(null);
  const hf = useHandsFree({ language: lang, onTranscript: useCallback((text) => { voiceRef.current = true; handleSendRef.current?.(text); }, []), onBargeIn: useCallback(() => stopSpeaking(), []) });
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const fileRef = useRef(null);
  const activeId = conversationId || null;

  const refreshConvs = useCallback(() => {
    api.listConversations().then(setConversations).catch(() => {});
  }, []);

  useEffect(() => { refreshConvs(); }, [refreshConvs]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, busy]);

  useEffect(() => {
    if (!conversationId) { if (!busy) setMessages([]); return; }
    if (location.state?.fresh) { navigate(location.pathname, { replace: true, state: {} }); return; }
    api.getConversation(conversationId)
      .then((c) => { setMessages(c.messages); setShared(!!c.shared); const m = MODELS.find((x) => x.id === c.model); if (m) setModel(m); })
      .catch(() => { toast.error(lang === 'fr' ? 'Conversation introuvable' : 'Conversation not found'); navigate('/chat'); });
    // Only re-run when the route param changes; other values are read fresh inside the callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    if (location.state?.initial) {
      const init = location.state.initial;
      navigate(location.pathname, { replace: true, state: { agent: location.state.agent } });
      handleSend(init);
    }
    // Mount-only: consume the initial message passed via router state exactly once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newChat = () => { abortRef.current?.abort(); stopSpeaking(); setAutoSpeakId(null); setMessages([]); setInput(''); setAgent(null); setPending([]); setShared(false); navigate('/chat'); };
  const selectConv = (id) => { abortRef.current?.abort(); stopSpeaking(); setAutoSpeakId(null); setAgent(null); navigate(`/chat/${id}`); };

  const deleteConv = async (id) => {
    try {
      await api.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) newChat();
      toast.success(lang === 'fr' ? 'Conversation supprimée' : 'Conversation deleted');
    } catch { toast.error(lang === 'fr' ? 'Suppression impossible' : 'Could not delete'); }
  };

  const stop = () => abortRef.current?.abort();

  const exportMd = () => {
    const title = conversations.find((c) => c.id === activeId)?.title || 'conversation';
    const md = `# ${title}\n\n` + messages.map((m) => `**${m.role === 'user' ? (lang === 'fr' ? 'Vous' : 'You') : 'Aurra'}**\n\n${m.content}${m.attachments?.length ? `\n\n_${m.attachments.map((a) => a.filename).join(', ')}_` : ''}`).join('\n\n---\n\n');
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/[^\w\dÀ-ÿ -]/g, '').trim().slice(0, 50) || 'aurra'}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(t.chat.exported);
  };
  const exportPdf = () => { setExportOpen(false); setTimeout(() => window.print(), 100); };

  const share = async () => {
    try {
      const r = await api.shareConversation(activeId);
      const url = `${window.location.origin}/share/${r.share_id}`;
      setShared(true);
      setConversations((prev) => prev.map((c) => (c.id === activeId ? { ...c, shared: true, share_id: r.share_id } : c)));
      try { await navigator.clipboard.writeText(url); } catch (e) { console.warn('[share] clipboard unavailable, link shown in toast', e); }
      toast.success(t.chat.shareCopied, { description: url, action: { label: t.chat.unshare, onClick: async () => { await api.unshareConversation(activeId); setShared(false); toast.success(t.chat.unshared); } }, duration: 10000 });
    } catch (e) { toast.error(e.message); }
  };

  const addFiles = async (files) => {
    for (const f of Array.from(files).slice(0, 5 - pending.length)) {
      const tmp = { id: 'tmp' + Math.random(), filename: f.name, content_type: f.type, uploading: true };
      setPending((p) => [...p, tmp]);
      try {
        const up = await api.upload(f);
        setPending((p) => p.map((x) => (x.id === tmp.id ? { ...up, uploading: false } : x)));
      } catch (e) {
        setPending((p) => p.filter((x) => x.id !== tmp.id));
        toast.error(e.message);
      }
    }
  };

  const handleSend = async (preset, opts = {}) => {
    const regenerate = !!opts.regenerate;
    const text = regenerate ? '' : (preset ?? input).trim();
    const files = regenerate ? [] : pending.filter((p) => !p.uploading);
    if (!regenerate && ((!text && files.length === 0) || pending.some((p) => p.uploading))) return;
    if (busy) return;
    if (!regenerate) { setInput(''); setPending([]); }
    setBusy(true);
    const userMsg = { id: 'u' + Date.now(), role: 'user', content: text, attachments: files };
    const asstId = 'a' + Date.now();
    setMessages((prev) => {
      if (regenerate) {
        const lastUser = [...prev].reverse().findIndex((m) => m.role === 'user');
        const cut = lastUser === -1 ? prev.length : prev.length - lastUser;
        return [...prev.slice(0, cut), { id: asstId, role: 'assistant', content: '' }];
      }
      return [...prev, userMsg, { id: asstId, role: 'assistant', content: '' }];
    });
    const controller = new AbortController();
    abortRef.current = controller;
    let convId = activeId;
    let full = '';
    try {
      await streamChat({ message: text, conversation_id: activeId, model: model.id, agent_id: agent?.id || null, attachment_ids: files.map((f) => f.id), regenerate, web_search: webSearch, image_mode: imageMode }, (ev) => {
        if (ev.type === 'tool') {
          setSearching(ev.name === 'image_generation' ? 'image' : ev.name === 'image_edit' ? 'edit' : true);
        } else if (ev.type === 'image') {
          setMessages((prev) => prev.map((m) => (m.id === asstId ? { ...m, attachments: [ev.attachment] } : m)));
        } else if (ev.type === 'delta') {
          setSearching(false);
        }
        if (ev.type === 'meta') {
          convId = ev.conversation_id;
          if (!activeId) navigate(`/chat/${convId}`, { replace: true, state: { fresh: true, agent } });
        } else if (ev.type === 'delta') {
          full += ev.content;
          setMessages((prev) => prev.map((m) => (m.id === asstId ? { ...m, content: m.content + ev.content } : m)));
        } else if (ev.type === 'title') {
          setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, title: ev.title } : c)));
        } else if (ev.type === 'error') {
          toast.error((lang === 'fr' ? 'Erreur IA : ' : 'AI error: ') + ev.message);
        }
      }, controller.signal);
    } catch (e) {
      if (e.status === 402) {
        toast.error(t.chat.quotaReached, { action: { label: t.account.upgrade, onClick: () => navigate('/upgrade') }, duration: 8000 });
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      } else if (e.name !== 'AbortError') toast.error(e.message || (lang === 'fr' ? 'Erreur réseau' : 'Network error'));
    } finally {
      setBusy(false);
      setSearching(false);
      if (voiceRef.current) {
        voiceRef.current = false;
        if (full.trim()) { setAutoSpeakId(asstId); hf.speaking(); } else hf.resume();
      }
      setMessages((prev) => prev.filter((m) => !(m.id === asstId && !m.content)));
      refreshConvs();
      if (user?.plan !== 'pro') refresh();
    }
  };

  const remaining = user && user.plan !== 'pro' ? Math.max(0, user.free_daily_limit - (user.usage_today ?? 0)) : null;
  handleSendRef.current = handleSend;
  const toggleHandsFree = () => { if (hf.active) { hf.stop(); stopSpeaking(); } else { setImageMode(false); hf.start(); } };
  const interruptSpeech = () => { stopSpeaking(); hf.resume(); };

  return (
    <div className="flex bg-background" data-testid="chat-page">
      <AppSidebar conversations={conversations} onNewChat={newChat} onSelectConv={selectConv} onDeleteConv={deleteConv} activeConvId={activeId} onConversationsChange={setConversations} showHistory />

      <main className="flex-1 flex flex-col h-screen min-w-0">
        <div className="h-16 border-b border-border flex items-center justify-between px-4 sm:px-6 shrink-0">
          <button onClick={() => navigate('/agents')} className="md:hidden h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setModelOpen((o) => !o)} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-accent transition-colors" data-testid="model-selector-btn">
                <span className="h-2 w-2 rounded-full bg-primary" />
                {model.name}
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
              {modelOpen && (
                <div className="absolute left-0 mt-2 w-60 rounded-xl border border-border bg-popover shadow-lg p-1.5 z-20 animate-fade-in" data-testid="model-dropdown">
                  {MODELS.map((m) => (
                    <button key={m.id} data-testid={`model-option-${m.id}`} onClick={() => { setModel(m); setModelOpen(false); }} className={`w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors ${model.id === m.id ? 'bg-accent' : ''}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{m.name}</span>
                        {m.badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">{m.badge}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{m.provider}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {agent && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary" data-testid="active-agent-badge">
                <Bot className="h-3.5 w-3.5" /> {lang === 'fr' ? agent.name : (agent.nameEn || agent.name)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeId && messages.length > 0 && (
              <>
                <div className="relative">
                  <Button variant="ghost" size="sm" data-testid="export-btn" onClick={() => setExportOpen((o) => !o)} className="rounded-lg font-semibold gap-1.5 text-muted-foreground">
                    <Download className="h-4 w-4" /> <span className="hidden sm:inline">{t.chat.export}</span>
                  </Button>
                  {exportOpen && (
                    <div className="absolute right-0 mt-2 w-44 rounded-xl border border-border bg-popover shadow-lg p-1.5 z-20 animate-fade-in" data-testid="export-menu">
                      <button data-testid="export-md-btn" onClick={() => { setExportOpen(false); exportMd(); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-accent text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Markdown (.md)</button>
                      <button data-testid="export-pdf-btn" onClick={exportPdf} className="w-full text-left px-3 py-2 rounded-lg hover:bg-accent text-sm flex items-center gap-2"><Printer className="h-4 w-4" /> PDF</button>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" data-testid="share-btn" onClick={share} className={`rounded-lg font-semibold gap-1.5 ${shared ? 'text-primary' : 'text-muted-foreground'}`}>
                  <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">{shared ? t.chat.shared : t.chat.share}</span>
                </Button>
              </>
            )}
            {remaining !== null && (
              <button onClick={() => navigate('/upgrade')} data-testid="quota-indicator" className={`hidden sm:flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors ${remaining <= 3 ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                <Crown className="h-3.5 w-3.5" /> {t.chat.remaining.replace('{n}', remaining)}
              </button>
            )}
            <Button variant="outline" size="sm" className="rounded-lg font-semibold" onClick={newChat} data-testid="new-chat-topbar-btn">{t.chat.newChat}</Button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto print-area">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center" data-testid="chat-empty-state">
              <span className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center mb-5">
                <Sparkles className="h-7 w-7 text-primary-foreground" />
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold">{t.chat.empty}</h2>
              <p className="mt-2 text-muted-foreground">{t.chat.emptySub}</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-3 w-full max-w-xl">
                {t.chat.suggestions.map((s, i) => (
                  <button key={s} data-testid={`suggestion-${i}`} onClick={() => handleSend(s)} className="text-left rounded-xl border border-border bg-card px-4 py-3 text-sm hover:border-primary/40 hover:bg-accent transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="messages-list">
              {messages.map((m, idx) => (
                <div key={m.id} data-testid={`message-${m.role}`} className={`flex gap-3.5 ${m.role === 'user' ? 'justify-end' : ''}`}>
                  {m.role === 'assistant' && (
                    <span className="h-8 w-8 shrink-0 rounded-lg bg-primary flex items-center justify-center"><Sparkles className="h-4 w-4 text-primary-foreground" /></span>
                  )}
                  <div className={`group max-w-[85%] min-w-0 ${m.role === 'user' ? 'order-1' : ''}`}>
                    <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary text-primary-foreground rounded-tr-sm whitespace-pre-wrap' : 'bg-secondary text-foreground rounded-tl-sm'}`}>
                      {m.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {m.attachments.map((a) => a.content_type?.startsWith('image/') && !String(a.id).startsWith('tmp')
                            ? <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" download={a.filename}><img src={fileUrl(a.id)} alt={a.filename} className={`rounded-lg object-cover ${m.role === 'assistant' ? 'max-h-96 w-full max-w-md' : 'max-h-48'}`} data-testid={m.role === 'assistant' ? 'generated-image' : 'attachment-image'} /></a>
                            : <AttachmentChip key={a.id} a={a} light />)}
                        </div>
                      )}
                      {m.role === 'assistant' ? (m.content ? <Markdown>{m.content}</Markdown> : (searching ? <span className="flex items-center gap-2 text-muted-foreground" data-testid="searching-indicator">{searching === 'image' || searching === 'edit' ? <ImageIcon className="h-3.5 w-3.5 animate-pulse text-primary" /> : <Globe className="h-3.5 w-3.5 animate-pulse text-primary" />} {searching === 'image' ? t.chat.generatingImage : searching === 'edit' ? t.chat.editingImage : t.chat.searching}</span> : <span className="typing-cursor" />)) : m.content}
                    </div>
                    {m.role === 'assistant' && m.content && (
                      <div className={`mt-1.5 flex items-center gap-3 transition-opacity ${autoSpeakId === m.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <button onClick={() => { navigator.clipboard.writeText(m.content); toast.success(lang === 'fr' ? 'Copié' : 'Copied'); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                          <Copy className="h-3 w-3" /> {lang === 'fr' ? 'Copier' : 'Copy'}
                        </button>
                        {idx === messages.length - 1 && activeId && !busy && (
                          <button data-testid="regenerate-btn" onClick={() => handleSend(null, { regenerate: true })} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                            <RefreshCw className="h-3 w-3" /> {t.chat.regenerate}
                          </button>
                        )}
                        <SpeakButton text={m.content} label={t.chat.listen} autoPlay={idx === messages.length - 1 && !busy && m.id === autoSpeakId} testid={`speak-btn-${idx}`} onEnd={() => { if (m.id === autoSpeakId) hf.resume(); }} />
                      </div>
                    )}
                  </div>
                  {m.role === 'user' && (
                    <span className="h-8 w-8 shrink-0 rounded-lg bg-secondary flex items-center justify-center"><User className="h-4 w-4" /></span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border p-4 shrink-0">
          {hf.active && <HandsFreeBar phase={hf.phase} level={hf.level} onStop={toggleHandsFree} onInterrupt={interruptSpeech} />}
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-border bg-card p-2 pl-2 focus-within:border-primary/50 transition-colors">
              {pending.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-2 pt-1 pb-2" data-testid="pending-attachments">
                  {pending.map((a) => <AttachmentChip key={a.id} a={a} onRemove={() => setPending((p) => p.filter((x) => x.id !== a.id))} />)}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.txt,.csv,.md,.json" className="hidden" data-testid="file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                <Button onClick={() => fileRef.current?.click()} size="icon" variant="ghost" className="rounded-xl h-10 w-10 shrink-0 text-muted-foreground" data-testid="attach-btn" title={t.chat.attach}><Paperclip className="h-4 w-4" /></Button>
                <Button onClick={() => setWebSearch((w) => !w)} size="icon" variant="ghost" aria-pressed={webSearch} className={`rounded-xl h-10 w-10 shrink-0 ${webSearch ? 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary' : 'text-muted-foreground'}`} data-testid="web-search-toggle" title={t.chat.webSearch}><Globe className="h-4 w-4" /></Button>
                <Button onClick={() => setImageMode((w) => !w)} size="icon" variant="ghost" aria-pressed={imageMode} className={`rounded-xl h-10 w-10 shrink-0 ${imageMode ? 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary' : 'text-muted-foreground'}`} data-testid="image-mode-toggle" title={t.chat.imageMode}><ImageIcon className="h-4 w-4" /></Button>
                <MicButton language={lang} disabled={busy || hf.active} title={t.chat.dictate} onTranscript={(text) => { voiceRef.current = true; handleSend(text); }} />
                <Button onClick={toggleHandsFree} size="icon" variant="ghost" aria-pressed={hf.active} data-testid="handsfree-toggle" title={t.chat.handsFree} className={`rounded-xl h-10 w-10 shrink-0 ${hf.active ? 'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground' : 'text-muted-foreground'}`}><Headphones className="h-4 w-4" /></Button>
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  onPaste={(e) => { const files = Array.from(e.clipboardData?.files || []); if (files.length) { e.preventDefault(); addFiles(files); } }}
                  rows={1}
                  placeholder={imageMode ? (pending.some((p) => p.content_type?.startsWith('image/')) ? t.chat.editPh : t.chat.imagePh) : t.chat.placeholder}
                  className="flex-1 bg-transparent outline-none resize-none text-sm max-h-32 py-2.5 placeholder:text-muted-foreground"
                />
                {busy ? (
                  <Button onClick={stop} size="icon" variant="secondary" className="rounded-xl h-10 w-10 shrink-0" data-testid="chat-stop-btn"><Square className="h-4 w-4" /></Button>
                ) : (
                  <Button onClick={() => handleSend()} disabled={(!input.trim() && pending.length === 0) || pending.some((p) => p.uploading)} size="icon" className="rounded-xl h-10 w-10 shrink-0" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2" data-testid="chat-footer-note">Aurra AI · {model.name}{webSearch && <span className="text-primary"> · {t.chat.webSearchOn}</span>}{imageMode && <span className="text-primary"> · {t.chat.imageModeOn}</span>}</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChatApp;
